import {
  desiredB2BAttribute,
  giftAddPayload,
  getQualifyingVariantId,
  hasGiftLine,
  orphanGiftKeys,
  shouldShowGiftPicker,
} from "mia-promo-logic";

const config = window.miaPromoConfig ?? {};
const strings = config.strings ?? {};
const GIFT_DIALOG_DISMISSED_KEY = "mia:gift-dialog-dismissed";

// The HulkApps Slide Cart drawer replaces Prestige's own drawer for shoppers. It
// renders at runtime, rebuilds itself on every cart change, and reaches the cart
// through its own requests without firing any theme event, so it needs both an
// injected picker and an external-mutation watcher.
const APP_DRAWER_SELECTOR = ".hs-popup-cart-sp-load";
const APP_DRAWER_BODY_SELECTOR = ".hs-layout-content";
const APP_DRAWER_LINE_SELECTOR = ".hs-item-cart-content";
const PICKER_TEMPLATE_ID = "mia-gift-picker-template";
const CART_MUTATION_PATTERN = /\/cart\/(add|change|update|clear)(\.js|\b)/;
const INTERNAL_REQUEST_HEADER = "X-Mia-Promo";
const observedAppDrawers = new WeakSet();
let b2bSyncStopped = false;
const CATALOG_RETRY_DELAYS = [400, 1200];
let catalogPromise;
let cartReloadRequested = false;
let giftAddInFlight = false;
let giftDialogDismissed = readGiftDialogDismissal();
let giftDialogEligibilityActive = false;
let giftDialogProgrammaticHide = false;
let lastCart;
let mutationChain = Promise.resolve();
const observedCartDrawers = new WeakSet();
const observedGiftDialogs = new WeakSet();
let reconcileActive = false;
let reconcilePending = false;
let renderGeneration = 0;

// Add `?mia_debug=1` to any storefront URL, or set localStorage "mia:debug" to
// "1", to trace what the promotion layer decided on this page view.
const debugEnabled = (() => {
  try {
    return (
      new URLSearchParams(window.location.search).has("mia_debug") ||
      window.localStorage.getItem("mia:debug") === "1"
    );
  } catch {
    return false;
  }
})();

function reportError(error) {
  console.error("[Mia promo]", error);
}

function debug(...args) {
  if (debugEnabled) console.info("[Mia promo]", ...args);
}

function errorMessage() {
  return strings.error ?? "Unable to add the free gift. Please try again.";
}

function announce(message) {
  const target = document.getElementById("status-announcement");
  if (target && message) target.textContent = message;
}

function readGiftDialogDismissal() {
  try {
    return window.sessionStorage.getItem(GIFT_DIALOG_DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

function setGiftDialogDismissal(dismissed) {
  giftDialogDismissed = dismissed;

  try {
    if (dismissed) {
      window.sessionStorage.setItem(GIFT_DIALOG_DISMISSED_KEY, "1");
    } else {
      window.sessionStorage.removeItem(GIFT_DIALOG_DISMISSED_KEY);
    }
  } catch {
    // Storage can be unavailable in restricted browsing contexts.
  }
}

function enqueueMutation(operation) {
  const result = mutationChain.then(operation, operation);
  mutationChain = result.catch(() => {});
  return result;
}

async function fetchCart() {
  const response = await fetch(`${Shopify.routes.root}cart.js`, {
    headers: { Accept: "application/json" },
  });
  if (!response.ok) throw new Error("Unable to read cart");
  return response.json();
}

function bundledSections() {
  const sections = [];
  document.documentElement.dispatchEvent(new CustomEvent(
    "cart:prepare-bundled-sections",
    { bubbles: true, detail: { sections } },
  ));

  return [...new Set(sections.filter(Boolean))];
}

function withBundledSections(payload) {
  const sections = bundledSections();
  if (sections.length === 0) return payload;

  return {
    ...payload,
    sections: sections.join(","),
    sections_url: `${window.location.pathname}${window.location.search}`,
  };
}

function dispatchCartChange(cart, baseEvent) {
  const cartDrawer = document.querySelector("cart-drawer");
  const drawerSectionId = cartDrawer
    ?.closest(".shopify-section")
    ?.id
    ?.replace(/^shopify-section-/, "");
  const hasDrawerSection = !cartDrawer || (
    cart?.sections &&
    typeof cart.sections === "object" &&
    (drawerSectionId
      ? typeof cart.sections[drawerSectionId] === "string"
      : Object.values(cart.sections).some((html) => typeof html === "string"))
  );

  if (!hasDrawerSection) {
    document.dispatchEvent(new CustomEvent("cart:refresh", {
      detail: { baseEvent },
    }));
    return;
  }

  document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
    bubbles: true,
    detail: { baseEvent, cart },
  }));
}

async function updateCart(payload) {
  const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      [INTERNAL_REQUEST_HEADER]: "1",
    },
    body: JSON.stringify(withBundledSections(payload)),
  });
  if (!response.ok) throw new Error("Unable to update cart");
  return response.json();
}

async function syncB2B(cart) {
  const desired = desiredB2BAttribute(config.isB2BCustomer);
  if (
    desired === null ||
    cart?.attributes?.B2B === desired ||
    b2bSyncStopped
  ) {
    debug("B2B attribute unchanged", {
      current: cart?.attributes?.B2B,
      desired,
      stopped: b2bSyncStopped,
    });
    return cart;
  }

  debug("writing B2B attribute", {
    current: cart?.attributes?.B2B,
    desired,
  });

  let lastError;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      return await updateCart({ attributes: { B2B: desired } });
    } catch (error) {
      lastError = error;
    }
  }

  b2bSyncStopped = true;
  reportError(lastError);
  return cart;
}

async function removeOrphanGifts(cart) {
  const keys = orphanGiftKeys(cart?.items);
  if (keys.length === 0) return cart;

  const updates = Object.fromEntries(keys.map((key) => [key, 0]));
  return updateCart({ updates });
}

function synchronizeQualifyingVariant(event) {
  const detailForm = event.detail?.formId;
  const form = event.target instanceof HTMLFormElement
    ? event.target
    : detailForm?.form instanceof HTMLFormElement
      ? detailForm.form
      : detailForm instanceof HTMLFormElement
        ? detailForm
        : typeof detailForm === "string"
          ? document.getElementById(detailForm)
          : null;
  const marker = form?.querySelector("[data-mia-qualifying-variant]");
  if (marker && event.detail?.variant?.id) {
    marker.value = String(event.detail.variant.id);
  }
}

function delay(ms) {
  return new Promise((resolve) => { window.setTimeout(resolve, ms); });
}

async function requestGiftCatalog() {
  let lastError;

  // A single failed fetch used to hide the picker until the next cart event,
  // which on a collection or home page may never come. Retry before giving up.
  for (let attempt = 0; attempt <= CATALOG_RETRY_DELAYS.length; attempt += 1) {
    if (attempt > 0) await delay(CATALOG_RETRY_DELAYS[attempt - 1]);

    try {
      const response = await fetch(`${config.giftCollectionUrl}?view=ajaxdata`);
      if (!response.ok) {
        throw new Error(`Unable to load gifts (${response.status})`);
      }

      const parsed = new DOMParser().parseFromString(
        await response.text(),
        "text/html",
      );
      const catalog = parsed.querySelector("[data-mia-gift-catalog]");
      if (!catalog) {
        throw new Error("Gift catalog markup missing from ?view=ajaxdata");
      }

      debug("gift catalog loaded", { attempt });
      return catalog.innerHTML;
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError;
}

function loadGiftCatalog() {
  if (!catalogPromise) {
    catalogPromise = requestGiftCatalog().catch((error) => {
      catalogPromise = undefined;
      throw error;
    });
  }

  return catalogPromise;
}

function relevantPicker(source) {
  const picker = source?.matches?.("[data-mia-gift-picker]")
    ? source
    : source?.closest?.("[data-mia-gift-picker]");
  if (picker?.isConnected) return picker;

  const pickers = [...document.querySelectorAll("[data-mia-gift-picker]")];
  return pickers.find((candidate) => !candidate.hidden) ?? pickers[0] ?? null;
}

function showPickerError(source, error) {
  const picker = relevantPicker(source);
  const status = picker?.querySelector("[data-mia-gift-status]");
  if (!picker || !status) return;

  picker.hidden = false;
  status.hidden = false;
  status.classList.remove("text-subdued");
  status.classList.add("banner", "banner--error");
  status.setAttribute("role", "alert");
  status.textContent = error?.message || errorMessage();
}

function clearPickerStatus(status) {
  if (!status) return;
  status.hidden = true;
  status.classList.add("text-subdued");
  status.classList.remove("banner", "banner--error");
  status.removeAttribute("role");
  status.textContent = strings.loading ?? "";
}

function setPickerLoading(status) {
  if (status) status.hidden = false;
}

function hideGiftDialog(dialog) {
  if (!dialog?.open || typeof dialog.hide !== "function") return;

  giftDialogProgrammaticHide = true;
  try {
    dialog.hide();
  } finally {
    giftDialogProgrammaticHide = false;
  }
}

function synchronizeGiftDialog(visible) {
  const dialog = document.querySelector("[data-mia-gift-dialog]");
  const cartDrawer = document.querySelector("cart-drawer");

  if (cartDrawer && !observedCartDrawers.has(cartDrawer)) {
    cartDrawer.addEventListener("dialog:before-show", () => {
      hideGiftDialog(document.querySelector("[data-mia-gift-dialog]"));
    });
    cartDrawer.addEventListener("dialog:after-hide", () => {
      if (giftDialogEligibilityActive) {
        setGiftDialogDismissal(true);
        hideGiftDialog(document.querySelector("[data-mia-gift-dialog]"));
      }
    });
    observedCartDrawers.add(cartDrawer);
  }

  if (!visible) {
    giftDialogEligibilityActive = false;
    setGiftDialogDismissal(false);
    hideGiftDialog(dialog);
    return;
  }

  giftDialogEligibilityActive = true;
  if (!dialog) return;

  if (!observedGiftDialogs.has(dialog)) {
    dialog.addEventListener("dialog:before-hide", () => {
      if (giftDialogEligibilityActive && !giftDialogProgrammaticHide) {
        setGiftDialogDismissal(true);
      }
    });
    observedGiftDialogs.add(dialog);
  }

  if (cartDrawer?.open) {
    hideGiftDialog(dialog);
    return;
  }

  // Intentional Impulse parity: on a product page the gift dialog opens by
  // itself the first time a cart becomes gift-eligible. A shopper dismissal
  // (or opening the cart drawer) is remembered for the session, so it opens at
  // most once per eligibility window.
  const picker = dialog.querySelector("[data-mia-gift-picker]");
  if (
    !giftDialogDismissed &&
    !dialog.open &&
    picker &&
    !picker.hidden &&
    typeof dialog.show === "function" &&
    window.themeVariables?.settings?.pageType === "product"
  ) {
    dialog.show();
  }
}

function isGiftPickerVisible(cart) {
  return shouldShowGiftPicker({
    enabled: config.enabled,
    isB2B: config.isB2BCustomer,
    hasQualifyingCollection: config.hasQualifyingCollection,
    giftCollectionUrl: config.giftCollectionUrl,
    items: cart?.items,
  });
}

function refreshAppDrawer() {
  try {
    if (typeof window.HS_SLIDE_CART_UPDATE === "function") {
      window.HS_SLIDE_CART_UPDATE();
      debug("asked app drawer to refresh");
    }
  } catch (error) {
    debug("app drawer refresh failed", error);
  }
}

// The app drawer has no post-render callback, so the picker is cloned in after
// every rebuild rather than rendered once.
function syncAppDrawerPicker(visible) {
  const wrapper = document.querySelector(APP_DRAWER_SELECTOR);
  if (!wrapper) return;

  const existing = wrapper.querySelector("[data-mia-gift-picker]");
  if (!visible) {
    existing?.remove();
    return;
  }
  if (existing) return;

  const template = document.getElementById(PICKER_TEMPLATE_ID);
  const picker = template?.content
    ?.cloneNode(true)
    ?.querySelector("[data-mia-gift-picker]");
  if (!picker) return;

  picker.dataset.miaAppDrawer = "true";
  const lines = wrapper.querySelectorAll(APP_DRAWER_LINE_SELECTOR);
  const anchor = lines[lines.length - 1];

  if (anchor) {
    anchor.insertAdjacentElement("afterend", picker);
  } else {
    (wrapper.querySelector(APP_DRAWER_BODY_SELECTOR) ?? wrapper).append(picker);
  }

  debug("injected picker into app drawer");
}

function handleAppDrawerRebuild() {
  const wrapper = document.querySelector(APP_DRAWER_SELECTOR);
  if (!wrapper || !lastCart || !isGiftPickerVisible(lastCart)) return;

  const picker = wrapper.querySelector("[data-mia-gift-picker]");
  const loaded = picker?.querySelector("[data-mia-gift-products]")?.dataset
    .loaded === "true";

  // Re-render only when the rebuild dropped the picker or its contents,
  // otherwise our own writes into the drawer would keep the observer spinning.
  if (picker && loaded) return;
  renderPickers(lastCart).catch(reportError);
}

let appDrawerSyncTimer;
const appDrawerObserver = new MutationObserver(() => {
  clearTimeout(appDrawerSyncTimer);
  appDrawerSyncTimer = setTimeout(handleAppDrawerRebuild, 200);
});

function observeAppDrawer() {
  const wrapper = document.querySelector(APP_DRAWER_SELECTOR);
  if (!wrapper || observedAppDrawers.has(wrapper)) return;

  appDrawerObserver.observe(wrapper, { childList: true, subtree: true });
  observedAppDrawers.add(wrapper);
  debug("watching app drawer for rebuilds");
}

// The app boots after the theme, so the drawer usually does not exist during the
// first reconcile. Watch for it, then stop watching to keep the page cheap.
function waitForAppDrawer() {
  if (!config.enabled || document.querySelector(APP_DRAWER_SELECTOR)) return;

  const observer = new MutationObserver(() => {
    if (!document.querySelector(APP_DRAWER_SELECTOR)) return;
    observer.disconnect();
    clearTimeout(timeout);
    observeAppDrawer();
    handleAppDrawerRebuild();
  });

  const timeout = setTimeout(() => observer.disconnect(), 30000);
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

// The app toggles `hs-open-drawer` on <html> when the drawer opens. That is a
// cheap, reliable signal to re-attach if the app replaced the whole drawer node.
function watchAppDrawerOpen() {
  if (!config.enabled) return;

  const observer = new MutationObserver(() => {
    if (!document.documentElement.classList.contains("hs-open-drawer")) return;
    observeAppDrawer();
    handleAppDrawerRebuild();
  });

  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["class"],
  });
}

let externalReconcileTimer;
function scheduleExternalReconcile(source) {
  clearTimeout(externalReconcileTimer);
  externalReconcileTimer = setTimeout(() => {
    debug("external cart mutation detected", source);
    requestReconcile();
  }, 350);
}

// The app mutates the cart directly and never emits a theme cart event, so the
// only reliable signal is the request itself. Our own writes carry a marker
// header and are skipped to avoid reconciling in a loop.
function watchExternalCartMutations() {
  const nativeFetch = window.fetch;
  if (typeof nativeFetch === "function") {
    window.fetch = function (input, init) {
      const url = typeof input === "string"
        ? input
        : (input instanceof Request ? input.url : String(input ?? ""));
      let internal = false;

      try {
        const source = init?.headers ??
          (input instanceof Request ? input.headers : undefined);
        internal = source
          ? new Headers(source).has(INTERNAL_REQUEST_HEADER)
          : false;
      } catch {
        internal = false;
      }

      const result = nativeFetch.apply(this, arguments);
      if (!internal && CART_MUTATION_PATTERN.test(url)) {
        result
          .then((response) => {
            if (response?.ok) scheduleExternalReconcile("fetch");
          })
          .catch(() => {});
      }

      return result;
    };
  }

  const nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this.miaPromoUrl = typeof url === "string" ? url : "";
    return nativeOpen.call(this, method, url, ...rest);
  };

  const nativeSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function (...args) {
    if (CART_MUTATION_PATTERN.test(this.miaPromoUrl ?? "")) {
      this.addEventListener("load", () => {
        if (this.status >= 200 && this.status < 300) {
          scheduleExternalReconcile("xhr");
        }
      });
    }

    return nativeSend.apply(this, args);
  };
}

async function renderPickers(cart) {
  // Every run claims a generation. Awaiting the catalog lets a newer cart land
  // first, and a superseded run must leave the DOM and the dialog untouched.
  renderGeneration += 1;
  const generation = renderGeneration;
  const isStale = () => generation !== renderGeneration;

  const visible = isGiftPickerVisible(cart);

  debug("render pickers", {
    generation,
    visible,
    mounted: document.querySelectorAll("[data-mia-gift-picker]").length,
    appDrawer: Boolean(document.querySelector(APP_DRAWER_SELECTOR)),
  });

  // Establish eligibility and drawer listeners before catalog loading so a
  // drawer close always counts for the current eligibility window.
  synchronizeGiftDialog(visible);
  syncAppDrawerPicker(visible);
  observeAppDrawer();

  for (const picker of document.querySelectorAll("[data-mia-gift-picker]")) {
    if (isStale()) return;
    picker.hidden = !visible;
    if (!visible) continue;

    const products = picker.querySelector("[data-mia-gift-products]");
    const status = picker.querySelector("[data-mia-gift-status]");
    clearPickerStatus(status);
    if (!products || products.dataset.loaded === "true") continue;

    setPickerLoading(status);
    try {
      const catalogHtml = await loadGiftCatalog();
      if (isStale()) return;
      if (
        !picker.isConnected ||
        !products.isConnected ||
        !picker.contains(products)
      ) {
        continue;
      }
      if (catalogHtml.trim() === "") {
        // The endpoint answered but the gift collection has nothing available.
        picker.hidden = true;
        debug("gift collection has no available variants");
        continue;
      }

      products.innerHTML = catalogHtml;
      products.dataset.loaded = "true";
      if (status) status.hidden = true;
    } catch (error) {
      if (isStale()) return;
      reportError(error);
      picker.hidden = true;
    }
  }

  synchronizeGiftDialog(visible);
}

async function reconcile() {
  // Always read inside the mutation queue so delayed cart events cannot apply
  // updates against an older cart response.
  let current = await fetchCart();
  let changed = false;
  let removedOrphan = false;

  try {
    const afterB2B = await syncB2B(current);
    if (afterB2B !== current) {
      current = afterB2B;
      changed = true;
    }
  } catch (error) {
    reportError(error);
  }

  try {
    const afterCleanup = await removeOrphanGifts(current);
    if (afterCleanup !== current) {
      current = afterCleanup;
      changed = true;
      removedOrphan = true;
    }
  } catch (error) {
    reportError(error);
  }

  lastCart = current;
  if (changed) dispatchCartChange(current, "mia:reconcile");
  if (
    removedOrphan &&
    window.themeVariables?.settings?.pageType === "cart" &&
    !cartReloadRequested
  ) {
    cartReloadRequested = true;
    window.location.reload();
    return current;
  }
  if (removedOrphan) refreshAppDrawer();
  await renderPickers(current);
  return current;
}

function requestReconcile() {
  if (reconcileActive) {
    reconcilePending = true;
    return;
  }

  reconcileActive = true;
  enqueueMutation(reconcile)
    .catch(reportError)
    .finally(() => {
      reconcileActive = false;
      if (reconcilePending) {
        reconcilePending = false;
        requestReconcile();
      }
    });
}

function addGift(button) {
  if (giftAddInFlight) return;
  giftAddInFlight = true;
  button.setAttribute("aria-busy", "true");

  enqueueMutation(async () => {
    const current = await fetchCart();

    // One gift per cart. A second picker can survive a stale render or a
    // second tab, so the freshly read cart is the only authority here.
    if (hasGiftLine(current?.items)) {
      lastCart = current;
      await renderPickers(current);
      return;
    }

    const qualifyingVariantId = getQualifyingVariantId(current?.items);
    const giftVariantId = button.dataset.miaGiftVariant;
    if (
      !/^\d+$/.test(giftVariantId ?? "") ||
      !/^\d+$/.test(qualifyingVariantId ?? "")
    ) {
      throw new Error(errorMessage());
    }

    const payload = withBundledSections(
      giftAddPayload(giftVariantId, qualifyingVariantId),
    );
    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
        [INTERNAL_REQUEST_HEADER]: "1",
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error(errorMessage());
    }

    const added = await response.json();
    const cart = await fetchCart();
    if (added?.sections) cart.sections = added.sections;
    lastCart = cart;
    announce(strings.added);

    if (window.themeVariables?.settings?.pageType === "cart") {
      cartReloadRequested = true;
      window.location.reload();
      return;
    }

    dispatchCartChange(cart, "mia:gift-add");
    refreshAppDrawer();
    await renderPickers(cart);
  })
    .catch((error) => {
      reportError(error);
      showPickerError(button, error);
      button.dispatchEvent(new CustomEvent("cart:error", {
        bubbles: true,
        detail: { error: error.message },
      }));
    })
    .finally(() => {
      giftAddInFlight = false;
      if (button.isConnected) button.removeAttribute("aria-busy");
    });
}

document.addEventListener("variant:change", synchronizeQualifyingVariant);
document.addEventListener("cart:change", (event) => {
  const baseEvent = event.detail?.baseEvent;
  if (typeof baseEvent === "string" && baseEvent.startsWith("mia:")) {
    lastCart = event.detail.cart;
    return;
  }
  requestReconcile();
});
document.addEventListener("cart:refresh", (event) => {
  const baseEvent = event.detail?.baseEvent;
  if (typeof baseEvent === "string" && baseEvent.startsWith("mia:")) return;
  requestReconcile();
});
document.addEventListener("cart-drawer:refreshed", () => {
  if (lastCart) renderPickers(lastCart).catch(reportError);
});
document.addEventListener("click", (event) => {
  const button = event.target instanceof Element
    ? event.target.closest("[data-mia-gift-variant]")
    : null;
  if (button) addGift(button);
});
window.addEventListener("pageshow", (event) => {
  if (event.persisted) requestReconcile();
});

watchExternalCartMutations();
waitForAppDrawer();
watchAppDrawerOpen();
requestReconcile();
